import React, { useState, useEffect } from 'react';
import { FileText, Activity, Zap, Shield, ChevronRight, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const STORAGE_KEY = 'msml_onboarding_dismissed';

const STEPS = [
  {
    icon: <FileText className="w-6 h-6" />,
    title: 'Paste an AI output',
    body: 'Drop any AI-generated text into the workspace — a coaching response, medical answer, support reply, or policy explanation.',
  },
  {
    icon: <Activity className="w-6 h-6" />,
    title: 'Analyze human impact',
    body: 'MSML instantly scores the text across 6 dimensions: Safety, Agency, Emotional Regulation, Clarity, Actionability, and Cognitive Load.',
  },
  {
    icon: <Zap className="w-6 h-6" />,
    title: 'Improve for Human Benefit',
    body: 'One click generates an optimized rewrite that preserves intent while lifting the weakest dimensions.',
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: 'Review the judge result',
    body: 'An independent judge verifies that the rewrite kept the original intent and didn\'t introduce new risks.',
  },
];

export function OnboardingOverlay() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) setVisible(true);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
  };

  if (!visible) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="onboarding-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={dismiss} />

      {/* Card */}
      <div className="relative w-full max-w-md bg-[var(--msml-panel)] border border-white/10 rounded-2xl overflow-hidden msml-slide-up">
        {/* Close */}
        <button onClick={dismiss} className="absolute top-4 right-4 text-[var(--msml-text-muted)] hover:text-white z-10" data-testid="onboarding-close">
          <X className="w-4 h-4" />
        </button>

        {/* Step indicator */}
        <div className="flex gap-1.5 px-6 pt-6">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-[var(--msml-primary)]' : 'bg-white/10'}`} />
          ))}
        </div>

        {/* Content */}
        <div className="p-6 pt-5 text-center">
          <div className="w-14 h-14 rounded-xl bg-[var(--msml-primary-muted)] flex items-center justify-center mx-auto mb-4 text-[var(--msml-primary)]">
            {current.icon}
          </div>
          <p className="text-xs text-[var(--msml-primary)] font-medium uppercase tracking-wider mb-2">Step {step + 1} of {STEPS.length}</p>
          <h3 className="text-lg font-semibold text-white mb-2">{current.title}</h3>
          <p className="text-sm text-[var(--msml-text-secondary)] leading-relaxed">{current.body}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between px-6 pb-6">
          <button onClick={dismiss} className="text-xs text-[var(--msml-text-muted)] hover:text-white transition-colors" data-testid="onboarding-skip">
            Skip tour
          </button>
          {isLast ? (
            <Button onClick={dismiss} className="msml-btn-primary text-sm" data-testid="onboarding-start">
              Get Started
            </Button>
          ) : (
            <Button onClick={() => setStep(s => s + 1)} className="msml-btn-primary text-sm" data-testid="onboarding-next">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
